import { IconEdit, IconEye, IconPlus } from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import { notifications } from '@mantine/notifications';
import docsMutation from '@/api/mutitations/docs';
import { extractFilterOptions, initialColumnFilters } from '@/components/Table/Table.functions';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Button } from '@mantine/core';
import PropTypes from 'prop-types';
import { ACCORD_TYPE } from '@/helpers/enums';
import Table from '../Table';
import AgentAgreementsView from '../AgentAgreementView';
import AgentAgreementsEdit from '../AgentAgreementEdit';
import AgentAgreementCreate from '../AgentAgreementCreate';

const AgentAgreements = (props) => {
  const {
    setKey, agent, id: agentId, key,
  } = props;

  const t = prepareTranslate();

  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure(false);
  const [editOpened, { open: editOpen, close: editClose }] = useDisclosure(false);

  const { agreements: data, personalDetails } = agent;
  const [selectedId, setSelectedId] = useState(null);
  const exportOptions = ['excel'];

  const handleViewClick = (id) => {
    setSelectedId(id);
    viewOpen();
  };

  const handleEditClick = (id) => {
    setSelectedId(id);
    editOpen();
  };

  // eslint-disable-next-line max-len
  const accordTypeLabel = (value) => ACCORD_TYPE.find((type) => type.value === value)?.label || value;

  const mockColumns = [
    {
      accessor: 'companyId.hebrew',
      title: 'חברה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'product',
      title: 'מוצר',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentNumber',
      title: 'מספר סוכן',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'accordTypeLabel',
      title: 'סוג הסכם',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentFeeMonthlyDeposit',
      title: 'נפרעים מהפקדה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentFeeMonthlyAccumulation',
      title: 'נפרעים מצבירה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentFeeAnnualDeposit',
      title: 'היקף מהפקדה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentFeeAnnualAccumulation',
      title: 'היקף מצבירה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'employeeAgentCommission',
      title: 'עמלת סוכן שכיר',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'actions',
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          action: (row) => handleViewClick(row),
          icon: <IconEye size={16} />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: 'עריכה',
          action: (row) => handleEditClick(row),
          icon: <IconEdit size={16} />,
          showLabel: true,
          isPrimary: true,
        },
      ],
    },
  ];

  const columnsInitialFilters = initialColumnFilters(mockColumns);
  const [filterOptions, setFilterOptions] = useState(columnsInitialFilters);
  const [columnFilters, setColumnFilters] = useState(columnsInitialFilters);

  const getData = async (start, end, query = {}, sort = {}) => {
    const result = { data: [], count: 0 };

    try {
      let filteredData = data
        .filter((item) => item.status === 'active')
        .map((item) => ({
          ...item,
          product: item.branchId?.map((b) => b.hebrew).join(', ') || '',
          accordTypeLabel: accordTypeLabel(item.accordType),
        }));

      if (query && Object.keys(query).length > 0) {
        // eslint-disable-next-line max-len
        filteredData = filteredData.filter((item) => Object.entries(query).every(([queryKey, value]) => {
          if (!value) return true;

          const keys = queryKey.split('.');
          let fieldValue = keys.reduce((acc, k) => (acc == null ? undefined : acc[k]), item);

          if (Array.isArray(fieldValue)) {
            fieldValue = fieldValue.map((f) => (typeof f === 'object' ? f.hebrew || '' : f)).join(', ');
          }

          const matchValue = value.toString().trim().toLowerCase();
          return fieldValue?.toString().trim().toLowerCase().includes(matchValue);
        }));
      }

      if (sort?.columnAccessor && sort?.direction) {
        filteredData.sort((a, b) => {
          const keys = sort.columnAccessor.split('.');
          const aValue = keys.reduce((acc, k) => acc?.[k], a);
          const bValue = keys.reduce((acc, k) => acc?.[k], b);

          if (typeof aValue === 'string') {
            return sort.direction === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          if (typeof aValue === 'number') {
            return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
          }
          return 0;
        });
      }

      const returnData = filteredData.map((item) => ({
        id: item._id,
        ...item,
        agentFeeAnnualDeposit: (() => {
          const tiers = item.agentFeeAnnualDeposit;
          if (!Array.isArray(tiers) || !tiers.length) return '-';
          if (tiers.length === 1) return tiers[0].rate;
          return `${tiers.length} מדרגות`;
        })(),
        agentFeeAnnualAccumulation: (() => {
          const tiers = item.agentFeeAnnualAccumulation;
          if (!Array.isArray(tiers) || !tiers.length) return '-';
          if (tiers.length === 1) return tiers[0].rate;
          return `${tiers.length} מדרגות`;
        })(),
      }));

      const newFilterOptions = extractFilterOptions(returnData, mockColumns);
      setFilterOptions(newFilterOptions);

      result.data = returnData;
      result.count = filteredData.length;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching agent commissions:', error);
    }

    return result;
  };

  const createFile = async (option) => {
    try {
      const filterQuery = {};
      Object.entries(columnFilters).forEach(([columnKey, value]) => {
        if (value) {
          filterQuery[columnKey] = value;
        }
      });

      const { data: allFilteredData } = await getData(0, 0, filterQuery);

      const convertedData = allFilteredData.map((row) => {
        const convertedRow = { ...row };
        Object.keys(convertedRow).forEach((k) => {
          if (typeof convertedRow[k] === 'number') {
            convertedRow[k] = String(convertedRow[k]);
          }
        });
        return convertedRow;
      });

      const requestColumns = mockColumns.filter((column) => column.title !== 'פעולות');

      const exportData = {
        fileType: option,
        service: 'agent',
        columns: requestColumns,
        titleArray: [''],
        data: convertedData || [],
        query: filterQuery,
      };

      const fileTitleBase = `הסכמים של הסוכן ${personalDetails?.fullName || ''}`;
      const fileExtension = option === 'pdf' ? '.pdf' : '.xlsx';
      const fileTitle = `${fileTitleBase}${fileExtension}`;
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.agencyCommissions.notification.errors.export.title'),
        message: t('pages.agencyCommissions.notification.errors.export.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error creating export file:', error);
      return null;
    }
  };
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <Button
        mb='md'
        w='8rem'
        size='xs'
        leftSection={<IconPlus />}
        onClick={open}
      >
        {t('components.viewAgentAgreements.labels.add')}
      </Button>

      <Table
        key={`${data}${key}`}
        isExportActive={true}
        columns={mockColumns}
        createFile={createFile}
        getData={getData}
        isColumnsActive={false}
        exportOptions={exportOptions}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        filterOptions={filterOptions}
      />

      <AgentAgreementCreate
        opened={opened} open={open} close={close} id={agentId} setKey={setKey}
      />

      <AgentAgreementsView
        opened={viewOpened}
        open={viewOpen}
        close={viewClose}
        id={selectedId}
        data={data}
      />

      <AgentAgreementsEdit
        open={editOpen}
        close={editClose}
        opened={editOpened}
        agreement={data?.find((a) => a._id === selectedId)}
        setKey={setKey}
        agentId={agentId}
      />
    </>
  );
};

AgentAgreements.propTypes = {
  id: PropTypes.string,
  setKey: PropTypes.func,
  agent: PropTypes.object,
  key: PropTypes.any,
};

export default AgentAgreements;
